import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, User, Building2, MessageSquare, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import FormInput from '../components/ui/FormInput';
import FormTextarea from '../components/ui/FormTextarea';

export default function RequestAccessPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { theme } = useTheme();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [motivation, setMotivation] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    setError('');
    setSubmitting(true);

    try {
      const response = await fetch('/api/access-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          company: company.trim() || null,
          motivation: motivation.trim(),
        }),
      });

      const data = await response.json();
      if (data.error) {
        setError(data.error);
      } else {
        setSubmitted(true);
      }
    } catch {
      setError(t('access_request.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-enter">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1
            className="text-4xl font-bold tracking-tight cursor-pointer"
            onClick={() => navigate('/')}
          >
            {theme.logo ? (
              <img src={theme.logo} alt={theme.name} className="h-12 mx-auto" />
            ) : (
              <span className="text-primary">{theme.name.toLowerCase()}</span>
            )}
          </h1>
        </div>

        <div className="glass-card p-8">
          {submitted ? (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-success mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">{t('access_request.success_title')}</h2>
              <p className="text-muted-foreground mb-6">
                {t('access_request.success_message')}
              </p>
              <button
                onClick={() => navigate('/')}
                className="btn-primary px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl transition-colors"
              >
                {t('access_request.back_home')}
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-foreground mb-2 text-center">
                {t('access_request.title')}
              </h2>
              <p className="text-muted-foreground text-sm text-center mb-6">
                {t('access_request.subtitle')}
              </p>

              {error && (
                <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg flex items-center gap-2 text-destructive text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <FormInput
                  icon={User}
                  label={`${t('access_request.name')} *`}
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  required
                />

                <FormInput
                  icon={Mail}
                  label={`${t('access_request.email')} *`}
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john@company.com"
                  required
                />

                <FormInput
                  icon={Building2}
                  label={t('access_request.company')}
                  type="text"
                  id="company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Acme Inc."
                />

                <FormTextarea
                  icon={MessageSquare}
                  label={`${t('access_request.motivation')} *`}
                  id="motivation"
                  value={motivation}
                  onChange={(e) => setMotivation(e.target.value)}
                  placeholder={t('access_request.motivation_placeholder')}
                  required
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full py-3 bg-primary hover:bg-primary/90 disabled:bg-primary/50 text-primary-foreground font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('access_request.submitting')}
                    </>
                  ) : (
                    t('access_request.submit')
                  )}
                </button>
              </form>

              <div className="mt-4 text-center space-y-2">
                <button
                  onClick={() => navigate('/login')}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors block mx-auto"
                >
                  {t('access_request.already_have_account')}
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="text-sm text-muted-foreground/60 hover:text-muted-foreground transition-colors block mx-auto"
                >
                  {t('access_request.back_home')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
